import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { toast } from "sonner";
import { ACCOUNTING_QUERY_KEYS } from "../../configs/queryKeys";
import {
  useBankMovementDetailQuery,
  useSettlementAllocationsQuery,
  useSettlementAccountsQuery,
  useSettlementPaymentMethodsQuery,
  useUpdateSettlement,
  useDeleteSettlement,
  useCreateSettlementLedgerAllocation,
  useUpdateSettlementLedgerAllocation,
  useDeleteSettlementLedgerAllocation,
} from "../../hooks/useAccountingQueries";
import { accountingService } from "../../services/accounting.service";
import { useCategorizationRules } from "../movement-allocations/useCategorizationRules";
import { useOperationTypes } from "../movement-allocations/useOperationTypes";
import {
  areNumbersEqual,
  extractApiErrorMessage,
  formatAmount,
  isServerRejection,
  normalizeSearchText,
  getPreferredOperationTypeNature,
  parseNullableNumber,
  resolveBudgetType,
  toEditableState,
} from "../movement-allocations/utils";
import type { BankMovement } from "../../core/models/admin";
import type { LedgerAllocation, Settlement } from "../../types/accounting.types";
import type { StockEditorSaveOutcome, StockEditorSavePayload } from "../movement-allocations/useMovementAllocationsState";
import type { EditableAllocationState, OperationTypeNature } from "../movement-allocations/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EditableSettlementState = {
  date: string
  number: string
  issuer: string
  paymentMethod: string
  amount: string
  deferredDate: string
  bankAccountId: string
};

export const toEditableSettlementState = (settlement: Settlement): EditableSettlementState => ({
  date: settlement.date ?? "",
  number: settlement.number ?? "",
  issuer: settlement.issuer ?? "",
  paymentMethod: settlement.paymentMethod ?? "",
  amount: settlement.amount !== null ? formatAmount(settlement.amount) : "",
  deferredDate: settlement.deferredDate ?? "",
  bankAccountId: settlement.bankAccount?.id ?? "",
});

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

type UseSettlementDrawerStateParams = {
  open: boolean
  onOpenChange: (open: boolean) => void
  settlement: Settlement | null
  onSettlementUpdated?: (settlement: Settlement) => void
  onSettlementDeleted?: () => void
};

export const useSettlementDrawerState = ({
  open,
  onOpenChange,
  settlement,
  onSettlementUpdated,
  onSettlementDeleted,
}: UseSettlementDrawerStateParams) => {
  const settlementId = settlement?.id ?? "";
  const bankMovementId = settlement?.bankMovementId ?? "";

  // ---------------------------------------------------------------------------
  // Settlement form state
  // ---------------------------------------------------------------------------

  const [settlementDraft, setSettlementDraft] = useState<EditableSettlementState | null>(null);
  const [accountPopoverOpen, setAccountPopoverOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [paymentMethodPopoverOpen, setPaymentMethodPopoverOpen] = useState(false);
  const [paymentMethodSearch, setPaymentMethodSearch] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Allocation draft state
  // ---------------------------------------------------------------------------

  const [draftValues, setDraftValues] = useState<Record<string, EditableAllocationState>>({});
  const [simpleEditorAllocationId, setSimpleEditorAllocationId] = useState<string | null>(null);
  const [reimbursementEditorAllocationId, setReimbursementEditorAllocationId] = useState<string | null>(null);
  const [ecommerceEditorAllocationId, setEcommerceEditorAllocationId] = useState<string | null>(null);
  const [stockEditorAllocationId, setStockEditorAllocationId] = useState<string | null>(null);
  const [operationTypeSearch, setOperationTypeSearch] = useState("");
  const [operationPopoverAllocationId, setOperationPopoverAllocationId] = useState<string | null>(null);
  const [operationPopoverSearchByAllocationId, setOperationPopoverSearchByAllocationId] = useState<Record<string, string>>({});
  const [simpleCategoryPopoverOpen, setSimpleCategoryPopoverOpen] = useState(false);
  const [simpleCategoryPopoverSearch, setSimpleCategoryPopoverSearch] = useState("");
  const [isSimpleCategorySearching, setIsSimpleCategorySearching] = useState(false);
  const [operationSearchBusyByAllocationId, setOperationSearchBusyByAllocationId] = useState<Record<string, boolean>>({});
  const operationSearchTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const simpleCategorySearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const simpleCategoryContainerRef = useRef<HTMLDivElement | null>(null);
  const [deletingAllocationId, setDeletingAllocationId] = useState<string | null>(null);
  const [isAddingAllocation, setIsAddingAllocation] = useState(false);
  const [pendingAddAllocationId, setPendingAddAllocationId] = useState<string | null>(null);
  const [refundSearchOpen, setRefundSearchOpen] = useState(false);
  const [ecommerceSearchOpen, setEcommerceSearchOpen] = useState(false);

  // Supplier state (simple editor)
  const [simpleEditorSuppliers, setSimpleEditorSuppliers] = useState<{ id: string, name: string }[]>([]);
  const [simpleEditorSupplierSearch, setSimpleEditorSupplierSearch] = useState("");
  const [simpleEditorSelectedSupplierId, setSimpleEditorSelectedSupplierId] = useState("");
  const [isSimpleEditorSupplierPopoverOpen, setIsSimpleEditorSupplierPopoverOpen] = useState(false);
  const [isLoadingSimpleEditorSuppliers, setIsLoadingSimpleEditorSuppliers] = useState(false);
  const simpleEditorSupplierSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const simpleEditorSupplierContainerRef = useRef<HTMLDivElement | null>(null);

  // ---------------------------------------------------------------------------
  // Linked movement state
  // ---------------------------------------------------------------------------

  const queryClient = useQueryClient();
  const [movementSearchQuery, setMovementSearchQuery] = useState("");
  const [movementSearchResults, setMovementSearchResults] = useState<BankMovement[]>([]);
  const [isSearchingMovement, setIsSearchingMovement] = useState(false);
  const movementDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [attachingMovementId, setAttachingMovementId] = useState<string | null>(null);
  const [isDetachingMovement, setIsDetachingMovement] = useState(false);

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  const { data: allocations = [], isLoading: isLoadingAllocations } = useSettlementAllocationsQuery(
    settlementId,
    open && Boolean(settlementId),
  );
  const { data: bankAccounts = [], isLoading: isBankAccountsLoading } = useSettlementAccountsQuery();
  const { data: paymentMethods = [], isLoading: isPaymentMethodsLoading } = useSettlementPaymentMethodsQuery();
  const { data: linkedMovement = null, isLoading: isLoadingLinkedMovement } = useBankMovementDetailQuery(
    settlement?.bankMovementId ?? null,
    open && Boolean(settlement?.bankMovementId),
  );

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const settlementUpdateMutation = useUpdateSettlement();
  const settlementDeleteMutation = useDeleteSettlement();
  const createMutation = useCreateSettlementLedgerAllocation(settlementId);
  const updateMutation = useUpdateSettlementLedgerAllocation(settlementId);
  const deleteMutation = useDeleteSettlementLedgerAllocation(settlementId);

  const isSettlementBusy = settlementUpdateMutation.isPending;
  const isDeleteBusy = settlementDeleteMutation.isPending;
  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // ---------------------------------------------------------------------------
  // Derived allocation state
  // ---------------------------------------------------------------------------

  const selectedSimpleAllocation = useMemo(
    () => allocations.find((a) => a.id === simpleEditorAllocationId) ?? null,
    [allocations, simpleEditorAllocationId],
  );
  const selectedReimbursementAllocation = useMemo(
    () => allocations.find((a) => a.id === reimbursementEditorAllocationId) ?? null,
    [allocations, reimbursementEditorAllocationId],
  );
  const selectedEcommerceAllocation = useMemo(
    () => allocations.find((a) => a.id === ecommerceEditorAllocationId) ?? null,
    [allocations, ecommerceEditorAllocationId],
  );
  const selectedStockAllocation = useMemo(
    () => allocations.find((a) => a.id === stockEditorAllocationId) ?? null,
    [allocations, stockEditorAllocationId],
  );

  const activeEditorAllocationId = simpleEditorAllocationId ?? reimbursementEditorAllocationId ?? ecommerceEditorAllocationId;
  const selectedSimpleBudgetType = useMemo<"ASC" | "AEP">(() => {
    if (!activeEditorAllocationId) return "ASC";
    const draftBudgetType = draftValues[activeEditorAllocationId]?.budgetType?.trim().toUpperCase();
    if (draftBudgetType === "AEP") return "AEP";
    if (draftBudgetType === "ASC") return "ASC";
    const activeAllocation = selectedSimpleAllocation ?? selectedReimbursementAllocation ?? selectedEcommerceAllocation;
    const persistedBudgetType = activeAllocation?.budgetType?.trim().toUpperCase();
    return persistedBudgetType === "AEP" ? "AEP" : "ASC";
  }, [draftValues, selectedSimpleAllocation?.budgetType, selectedReimbursementAllocation?.budgetType, selectedEcommerceAllocation?.budgetType, activeEditorAllocationId]);

  // ---------------------------------------------------------------------------
  // Sub-hooks
  // ---------------------------------------------------------------------------

  const onOperationTypeRemoved = useCallback((operationTypeId: string) => {
    if (!activeEditorAllocationId) return;
    const currentOpTypeId = draftValues[activeEditorAllocationId]?.operationTypeId;
    if (currentOpTypeId !== operationTypeId) return;
    setDraftValues((prev) => {
      const alloc = allocations.find((a) => a.id === activeEditorAllocationId);
      if (!alloc) return prev;
      const current = prev[activeEditorAllocationId] ?? toEditableState(alloc);
      return { ...prev, [activeEditorAllocationId]: { ...current, operationTypeId: "", operationType: "" } };
    });
    setOperationTypeSearch("");
  }, [activeEditorAllocationId, draftValues, allocations]);

  const opTypes = useOperationTypes({
    bankMovementId,
    open,
    selectedSimpleBudgetType,
    onOperationTypeRemoved,
  });

  const rules = useCategorizationRules({
    open,
    ascOperationTypes: opTypes.ascOperationTypes,
    aepOperationTypes: opTypes.aepOperationTypes,
  });

  // ---------------------------------------------------------------------------
  // Allocation totals
  // ---------------------------------------------------------------------------

  const totalDebit = useMemo(() => allocations.reduce((acc, a) => acc + (a.debit ?? 0), 0), [allocations]);
  const totalCredit = useMemo(() => allocations.reduce((acc, a) => acc + (a.credit ?? 0), 0), [allocations]);
  const totalToAllocate = settlement?.amount ?? 0;
  const remainingToAllocate = totalToAllocate - totalCredit + totalDebit;

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (settlement && open) {
      setSettlementDraft(toEditableSettlementState(settlement));
    }
  }, [settlement?.id, open]);

  useEffect(() => {
    if (!open) {
      setSettlementDraft(null);
      setAccountPopoverOpen(false);
      setAccountSearch("");
      setPaymentMethodPopoverOpen(false);
      setPaymentMethodSearch("");
      setIsDeleteDialogOpen(false);
      setDraftValues({});
      setSimpleEditorAllocationId(null);
      setReimbursementEditorAllocationId(null);
      setEcommerceEditorAllocationId(null);
      setStockEditorAllocationId(null);
      setOperationPopoverAllocationId(null);
      setOperationPopoverSearchByAllocationId({});
      setSimpleCategoryPopoverOpen(false);
      setSimpleCategoryPopoverSearch("");
      setSimpleEditorSuppliers([]);
      setSimpleEditorSupplierSearch("");
      setSimpleEditorSelectedSupplierId("");
      setIsSimpleEditorSupplierPopoverOpen(false);
      setMovementSearchQuery("");
      setMovementSearchResults([]);
      setIsSearchingMovement(false);
    }
  }, [open]);

  useEffect(() => {
    setDraftValues({});
  }, [settlementId]);

  useEffect(() => {
    if (deletingAllocationId && !allocations.some((a) => a.id === deletingAllocationId)) {
      setDeletingAllocationId(null);
    }
  }, [allocations, deletingAllocationId]);

  useEffect(() => {
    if (pendingAddAllocationId && allocations.some((a) => a.id === pendingAddAllocationId)) {
      setIsAddingAllocation(false);
      setPendingAddAllocationId(null);
    }
  }, [allocations, pendingAddAllocationId]);

  useEffect(() => {
    return () => {
      Object.values(operationSearchTimersRef.current).forEach((t) => clearTimeout(t));
      if (simpleCategorySearchTimerRef.current) clearTimeout(simpleCategorySearchTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!simpleCategoryPopoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (!simpleCategoryContainerRef.current?.contains(e.target as Node)) {
        setSimpleCategoryPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [simpleCategoryPopoverOpen]);

  useEffect(() => {
    if (!isSimpleEditorSupplierPopoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (!simpleEditorSupplierContainerRef.current?.contains(e.target as Node)) {
        setIsSimpleEditorSupplierPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isSimpleEditorSupplierPopoverOpen]);

  useEffect(() => {
    if (!operationPopoverAllocationId) return;
    setOperationPopoverSearchByAllocationId((prev) => ({
      ...prev,
      [operationPopoverAllocationId]: prev[operationPopoverAllocationId] ?? "",
    }));
  }, [operationPopoverAllocationId]);

  useEffect(() => {
    if (!selectedSimpleAllocation) {
      if (!selectedReimbursementAllocation) {
        setOperationTypeSearch("");
        setSimpleCategoryPopoverSearch("");
        setSimpleCategoryPopoverOpen(false);
        setIsSimpleCategorySearching(false);
      }
      return;
    }
    setOperationTypeSearch(selectedSimpleAllocation.operationTypeName ?? selectedSimpleAllocation.operationType ?? "");
    setSimpleCategoryPopoverSearch("");
    setSimpleEditorSelectedSupplierId(selectedSimpleAllocation.supplierId ?? "");
    setSimpleEditorSupplierSearch(selectedSimpleAllocation.supplierName ?? "");
    loadSimpleEditorSuppliers("");
  }, [selectedSimpleAllocation?.id]);

  useEffect(() => {
    if (!selectedReimbursementAllocation) {
      if (!selectedSimpleAllocation) {
        setOperationTypeSearch("");
        setSimpleCategoryPopoverSearch("");
        setSimpleCategoryPopoverOpen(false);
        setIsSimpleCategorySearching(false);
      }
      return;
    }
    setOperationTypeSearch(selectedReimbursementAllocation.operationTypeName ?? selectedReimbursementAllocation.operationType ?? "");
    setSimpleCategoryPopoverSearch("");
    setSimpleEditorSelectedSupplierId(selectedReimbursementAllocation.supplierId ?? "");
    setSimpleEditorSupplierSearch(selectedReimbursementAllocation.supplierName ?? "");
    loadSimpleEditorSuppliers("");
  }, [selectedReimbursementAllocation?.id]);

  useEffect(() => {
    if (!selectedEcommerceAllocation) {
      if (!selectedSimpleAllocation && !selectedReimbursementAllocation) {
        setOperationTypeSearch("");
        setSimpleCategoryPopoverSearch("");
        setSimpleCategoryPopoverOpen(false);
        setIsSimpleCategorySearching(false);
      }
      return;
    }
    setOperationTypeSearch(selectedEcommerceAllocation.operationTypeName ?? selectedEcommerceAllocation.operationType ?? "");
    setSimpleCategoryPopoverSearch("");
    setSimpleEditorSelectedSupplierId(selectedEcommerceAllocation.supplierId ?? "");
    setSimpleEditorSupplierSearch(selectedEcommerceAllocation.supplierName ?? "");
    loadSimpleEditorSuppliers("");
  }, [selectedEcommerceAllocation?.id]);

  // ---------------------------------------------------------------------------
  // Derived settlement state
  // ---------------------------------------------------------------------------

  const selectedAccount = useMemo(() => {
    const id = settlementDraft?.bankAccountId;
    if (!id) return null;
    return bankAccounts.find((a) => a.id === id) ?? null;
  }, [settlementDraft?.bankAccountId, bankAccounts]);

  const selectedAccountBudgetType = useMemo<"ASC" | "AEP">(() => {
    const bt = selectedAccount?.budgetType?.trim().toUpperCase();
    return bt === "AEP" ? "AEP" : "ASC";
  }, [selectedAccount?.budgetType]);

  const filteredBankAccounts = useMemo(() => {
    const q = normalizeSearchText(accountSearch);
    if (!q) return bankAccounts;
    return bankAccounts.filter((a) => normalizeSearchText(a.name).includes(q));
  }, [accountSearch, bankAccounts]);

  const filteredPaymentMethods = useMemo(() => {
    const q = normalizeSearchText(paymentMethodSearch);
    if (!q) return paymentMethods;
    return paymentMethods.filter((m) => normalizeSearchText(m).includes(q));
  }, [paymentMethodSearch, paymentMethods]);

  const canSaveSettlement = useMemo(() => {
    if (!settlement || !settlementDraft) return false;
    const original = toEditableSettlementState(settlement);
    return (
      settlementDraft.date !== original.date
      || settlementDraft.number !== original.number
      || settlementDraft.issuer !== original.issuer
      || settlementDraft.paymentMethod !== original.paymentMethod
      || settlementDraft.deferredDate !== original.deferredDate
      || settlementDraft.bankAccountId !== original.bankAccountId
      || !areNumbersEqual(parseNullableNumber(settlementDraft.amount), parseNullableNumber(original.amount))
    );
  }, [settlement, settlementDraft]);

  // ---------------------------------------------------------------------------
  // Allocation helpers
  // ---------------------------------------------------------------------------

  function setField(id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) {
    setDraftValues((prev) => {
      const current = prev[id] ?? toEditableState(fallback);
      return { ...prev, [id]: { ...current, [field]: value } };
    });
  }

  /**
   * Refus du serveur : rien n'est parti en base, mais le brouillon gardait la
   * valeur refusée à l'écran, impossible à distinguer d'une valeur enregistrée.
   * Même règle que dans le drawer d'un mouvement (DRW-MVT).
   */
  function revertAllocationDraft(allocationId: string) {
    setDraftValues((prev) => {
      if (!(allocationId in prev)) return prev;
      const next = { ...prev };
      delete next[allocationId];
      return next;
    });
  }

  function getState(allocation: LedgerAllocation): EditableAllocationState {
    return draftValues[allocation.id] ?? toEditableState(allocation);
  }

  function isAllocationDirty(allocation: LedgerAllocation): boolean {
    const draft = draftValues[allocation.id];
    if (!draft) return false;
    const currentDebit = parseNullableNumber(draft.debit);
    const currentCredit = parseNullableNumber(draft.credit);
    return (
      (draft.date || "") !== (allocation.date || "")
      || (draft.budgetType || "") !== (allocation.budgetType || "")
      || (draft.operationType || "") !== (allocation.operationTypeName || allocation.operationType || "")
      || (draft.description || "") !== (allocation.description || "")
      || (draft.operationTypeId || "") !== (allocation.operationTypeId || "")
      || !areNumbersEqual(currentDebit, allocation.debit)
      || !areNumbersEqual(currentCredit, allocation.credit)
    );
  }

  const simpleEditorRow = selectedSimpleAllocation ? getState(selectedSimpleAllocation) : null;
  const reimbursementEditorRow = selectedReimbursementAllocation ? getState(selectedReimbursementAllocation) : null;
  const ecommerceEditorRow = selectedEcommerceAllocation ? getState(selectedEcommerceAllocation) : null;

  const findExactOperationTypeForBudget = (
    budgetType: "ASC" | "AEP",
    query: string,
    preferredNature: OperationTypeNature | null = null,
    currentOperationTypeId = "",
  ) => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return null;
    const matches = opTypes.operationTypesByBudget[budgetType].filter((ot) => normalizeSearchText(ot.name) === normalizedQuery);
    // Une catégorie peut exister en dépense et en recette sous le même nom : l'homonyme déjà choisi
    // prime, sinon on retient celui du sens de l'écriture.
    return matches.find((ot) => ot.id === currentOperationTypeId)
      ?? matches.find((ot) => preferredNature && (ot.nature ?? "expense") === preferredNature)
      ?? matches[0]
      ?? null;
  };

  const markOperationSearchBusy = (allocationId: string) => {
    setOperationSearchBusyByAllocationId((prev) => ({ ...prev, [allocationId]: true }));
    if (operationSearchTimersRef.current[allocationId]) clearTimeout(operationSearchTimersRef.current[allocationId]);
    operationSearchTimersRef.current[allocationId] = setTimeout(() => {
      setOperationSearchBusyByAllocationId((prev) => ({ ...prev, [allocationId]: false }));
    }, 250);
  };

  const markSimpleCategorySearchBusy = () => {
    setIsSimpleCategorySearching(true);
    if (simpleCategorySearchTimerRef.current) clearTimeout(simpleCategorySearchTimerRef.current);
    simpleCategorySearchTimerRef.current = setTimeout(() => setIsSimpleCategorySearching(false), 250);
  };

  // ---------------------------------------------------------------------------
  // Supplier helpers
  // ---------------------------------------------------------------------------

  const loadSimpleEditorSuppliers = useCallback((search: string) => {
    setIsLoadingSimpleEditorSuppliers(true);
    accountingService.searchSuppliers(search)
      .then((suppliers) => {
        setSimpleEditorSuppliers(suppliers);
      })
      .catch(() => {})
      .finally(() => setIsLoadingSimpleEditorSuppliers(false));
  }, []);

  const handleSimpleEditorSupplierSearchChange = (value: string) => {
    setSimpleEditorSupplierSearch(value);
    if (simpleEditorSupplierSearchTimerRef.current) clearTimeout(simpleEditorSupplierSearchTimerRef.current);
    simpleEditorSupplierSearchTimerRef.current = setTimeout(() => loadSimpleEditorSuppliers(value), 300);
  };

  // ---------------------------------------------------------------------------
  // Settlement handlers
  // ---------------------------------------------------------------------------

  const setSettlementField = useCallback((field: keyof EditableSettlementState, value: string) => {
    setSettlementDraft((prev) => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const handleSaveSettlement = useCallback(async () => {
    if (!settlement || !settlementDraft) return;
    try {
      await settlementUpdateMutation.mutateAsync({
        id: settlement.id,
        payload: {
          date: settlementDraft.date || null,
          number: settlementDraft.number || null,
          issuer: settlementDraft.issuer || null,
          paymentMethod: settlementDraft.paymentMethod || null,
          amount: parseNullableNumber(settlementDraft.amount),
          deferredDate: settlementDraft.deferredDate || null,
          bankAccountId: settlementDraft.bankAccountId || null,
        },
      });
      toast.success("Règlement enregistré");
      if (onSettlementUpdated) {
        onSettlementUpdated({
          ...settlement,
          date: settlementDraft.date || null,
          number: settlementDraft.number || null,
          issuer: settlementDraft.issuer || null,
          paymentMethod: settlementDraft.paymentMethod || null,
          amount: parseNullableNumber(settlementDraft.amount),
          deferredDate: settlementDraft.deferredDate || null,
          bankAccount: selectedAccount ? { id: selectedAccount.id, name: selectedAccount.name } : settlement.bankAccount,
        });
      }
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
  }, [settlement, settlementDraft, settlementUpdateMutation, onSettlementUpdated, selectedAccount]);

  const handleDeleteSettlement = useCallback(async () => {
    if (!settlement) return;
    try {
      await settlementDeleteMutation.mutateAsync(settlement.id);
      toast.success("Règlement supprimé");
      setIsDeleteDialogOpen(false);
      onOpenChange(false);
      onSettlementDeleted?.();
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
  }, [settlement, settlementDeleteMutation, onOpenChange, onSettlementDeleted]);

  // ---------------------------------------------------------------------------
  // Linked movement handlers
  // ---------------------------------------------------------------------------

  const runMovementSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setMovementSearchResults([]);
      setIsSearchingMovement(false);
      return;
    }
    setIsSearchingMovement(true);
    try {
      const result = await accountingService.listBankMovements({
        search: query.trim(),
        limit: 20,
      });
      setMovementSearchResults(result.bankMovements);
    }
    catch {
      setMovementSearchResults([]);
    }
    finally {
      setIsSearchingMovement(false);
    }
  }, []);

  const handleMovementSearchChange = useCallback((value: string) => {
    setMovementSearchQuery(value);
    if (movementDebounceRef.current) clearTimeout(movementDebounceRef.current);
    if (!value.trim()) {
      setMovementSearchResults([]);
      setIsSearchingMovement(false);
      return;
    }
    setIsSearchingMovement(true);
    movementDebounceRef.current = setTimeout(() => {
      void runMovementSearch(value);
    }, 300);
  }, [runMovementSearch]);

  const handleAttachMovement = useCallback(async (movementId: string) => {
    if (!settlement) return;
    setAttachingMovementId(movementId);
    try {
      await settlementUpdateMutation.mutateAsync({
        id: settlement.id,
        payload: { bankMovementId: movementId },
      });
      setMovementSearchResults([]);
      setMovementSearchQuery("");
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      toast.success("Mouvement associé au règlement");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setAttachingMovementId(null);
    }
  }, [settlement, settlementUpdateMutation, queryClient]);

  const handleDetachMovement = useCallback(async () => {
    if (!settlement) return;
    setIsDetachingMovement(true);
    try {
      await settlementUpdateMutation.mutateAsync({
        id: settlement.id,
        payload: { bankMovementId: null },
      });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      toast.success("Mouvement dissocié du règlement");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setIsDetachingMovement(false);
    }
  }, [settlement, settlementUpdateMutation, queryClient]);

  // ---------------------------------------------------------------------------
  // Allocation handlers
  // ---------------------------------------------------------------------------

  const handleSave = async (allocation: LedgerAllocation) => {
    const state = getState(allocation);
    try {
      await updateMutation.mutateAsync({
        id: allocation.id,
        payload: {
          bankMovementId: allocation.bankMovementId ?? bankMovementId,
          companyAccountId: settlementDraft?.bankAccountId || settlement?.bankAccount?.id || null,
          date: state.date || null,
          budgetType: state.budgetType || null,
          operationType: state.operationType || null,
          operationTypeId: state.operationTypeId || null,
          description: state.description || null,
          debit: parseNullableNumber(state.debit),
          credit: parseNullableNumber(state.credit),
        },
      });
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
      if (isServerRejection(error)) revertAllocationDraft(allocation.id);
    }
  };

  const handleFindOrCreateOperationTypeForAllocation = async (
    allocation: LedgerAllocation,
    forcedQuery?: string,
    forcedNature?: OperationTypeNature,
  ) => {
    const state = getState(allocation);
    const query = ((forcedQuery ?? state.operationType) || "").trim();
    if (!query) return null;
    const budgetType = resolveBudgetType(state.budgetType || allocation.budgetType);
    const nature = forcedNature ?? getPreferredOperationTypeNature(state.debit, state.credit);
    const match = findExactOperationTypeForBudget(budgetType, query, nature, state.operationTypeId);
    // Une création demandée dans une nature ne doit pas retomber sur l'homonyme de l'autre.
    const exactMatch = forcedNature && match && (match.nature ?? "expense") !== forcedNature ? null : match;
    if (exactMatch) {
      setField(allocation.id, "operationTypeId", exactMatch.id, allocation);
      setField(allocation.id, "operationType", exactMatch.name, allocation);
      setOperationPopoverAllocationId(null);
      return exactMatch;
    }
    try {
      const movementId = await ensureBankMovement();
      if (!movementId) {
        toast.error("Impossible de créer le mouvement bancaire associé");
        return null;
      }
      const created = await opTypes.createOperationTypeMutation.mutateAsync({
        name: query, budgetType, nature: nature ?? undefined, bankMovementId: movementId,
      });
      if (created?.id) {
        setField(allocation.id, "operationTypeId", created.id, allocation);
        setField(allocation.id, "operationType", created.name, allocation);
        setOperationPopoverAllocationId(null);
      }
      return created;
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
      return null;
    }
  };

  const ensureBankMovement = async (): Promise<string | null> => {
    if (bankMovementId) return bankMovementId;
    if (!settlement) return null;

    const updated = await accountingService.createSettlementMovement(settlement.id);
    if (!updated?.bankMovementId) return null;

    queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
    queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });

    return updated.bankMovementId;
  };

  const handleAdd = async (type: "simple" | "activity" | "stock" = "simple") => {
    const companyAccountId = settlementDraft?.bankAccountId || settlement?.bankAccount?.id || null;
    if (!companyAccountId) {
      toast.error("Compte requis pour créer une affectation");
      return;
    }
    if (type !== "stock" && remainingToAllocate <= 0) {
      toast.error("Ce règlement est déjà affecté à 100 %");
      return;
    }
    const normalizedRemaining = Number(Math.max(remainingToAllocate, 0).toFixed(2));
    let budgetType: string;
    let nextDebit: number;
    let nextCredit: number;
    if (type === "stock") {
      budgetType = "STOCK";
      nextDebit = 0;
      nextCredit = 0;
    }
    else {
      // Seule l'écriture simple suit le compte : les autres affectations partent en ASC (YTI-1647).
      budgetType = type === "simple" ? selectedAccountBudgetType : "ASC";
      nextDebit = 0;
      nextCredit = normalizedRemaining;
    }
    setIsAddingAllocation(true);
    const toastId = toast.loading("Ajout de l'affectation...");
    try {
      const movementId = await ensureBankMovement();
      if (!movementId) {
        toast.error("Impossible de créer le mouvement bancaire associé", { id: toastId });
        setIsAddingAllocation(false);
        return;
      }

      const createdAllocation = await createMutation.mutateAsync({
        bankMovementId: movementId,
        companyAccountId,
        budgetType,
        debit: nextDebit,
        credit: nextCredit,
        date: settlement?.date ?? null,
        settlementId,
      });
      setPendingAddAllocationId(createdAllocation.id);
      toast.success("Affectation ajoutée", { id: toastId });
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error), { id: toastId });
      setIsAddingAllocation(false);
    }
  };

  const handleAddRefund = async (activityOperationId: number, operationData: {
    debit: number | null
    credit: number | null
    allocationBalance: number | null
    label: string
  }) => {
    const companyAccountId = settlementDraft?.bankAccountId || settlement?.bankAccount?.id || null;
    if (!companyAccountId) {
      toast.error("Compte requis");
      return;
    }
    const balance = Math.abs(operationData.allocationBalance ?? 0);
    const remaining = Number(Math.max(remainingToAllocate, 0).toFixed(2));
    const amount = Math.min(balance, remaining);
    setIsAddingAllocation(true);
    const toastId = toast.loading("Ajout de l'affectation remboursement...");
    try {
      const movementId = await ensureBankMovement();
      if (!movementId) {
        toast.error("Impossible de créer le mouvement bancaire associé", { id: toastId });
        setIsAddingAllocation(false);
        return;
      }
      const created = await createMutation.mutateAsync({
        bankMovementId: movementId, companyAccountId, budgetType: "ASC",
        debit: 0, credit: amount, date: settlement?.date ?? null,
        description: operationData.label ?? null, activityOperationId, settlementId,
      });
      setPendingAddAllocationId(created.id);
      setRefundSearchOpen(false);
      toast.success("Affectation remboursement ajoutée", { id: toastId });
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error), { id: toastId });
      setIsAddingAllocation(false);
    }
  };

  const handleAddEcommerceOperation = async (activityOperationId: number, operationData: {
    debit: number | null
    credit: number | null
    allocationBalance: number | null
    label: string
  }) => {
    const companyAccountId = settlementDraft?.bankAccountId || settlement?.bankAccount?.id || null;
    if (!companyAccountId) {
      toast.error("Compte requis");
      return;
    }
    const balance = Math.abs(operationData.allocationBalance ?? 0);
    const remaining = Number(Math.max(remainingToAllocate, 0).toFixed(2));
    const amount = Math.min(balance, remaining);
    setIsAddingAllocation(true);
    const toastId = toast.loading("Ajout de l'affectation bons plans...");
    try {
      const movementId = await ensureBankMovement();
      if (!movementId) {
        toast.error("Impossible de créer le mouvement bancaire associé", { id: toastId });
        setIsAddingAllocation(false);
        return;
      }
      const created = await createMutation.mutateAsync({
        bankMovementId: movementId, companyAccountId, budgetType: "ASC",
        debit: 0, credit: amount, date: settlement?.date ?? null,
        description: operationData.label ?? null, activityOperationId, settlementId,
      });
      setPendingAddAllocationId(created.id);
      setEcommerceSearchOpen(false);
      toast.success("Affectation bons plans ajoutée", { id: toastId });
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error), { id: toastId });
      setIsAddingAllocation(false);
    }
  };

  const closeSimpleEditor = () => setSimpleEditorAllocationId(null);
  const closeReimbursementEditor = () => setReimbursementEditorAllocationId(null);
  const closeEcommerceEditor = () => setEcommerceEditorAllocationId(null);
  const closeStockEditor = () => setStockEditorAllocationId(null);

  const getActiveEditorAllocation = () => selectedSimpleAllocation ?? selectedReimbursementAllocation ?? selectedEcommerceAllocation;

  const handleFindOrCreateOperationType = async () => {
    const activeAllocation = getActiveEditorAllocation();
    if (!activeAllocation) return null;
    const query = operationTypeSearch.trim();
    if (!query) return null;
    const activeState = getState(activeAllocation);
    const budgetType = resolveBudgetType(activeState.budgetType || activeAllocation.budgetType);
    const nature = getPreferredOperationTypeNature(activeState.debit, activeState.credit);
    const exactMatch = findExactOperationTypeForBudget(budgetType, query, nature, activeState.operationTypeId);
    if (exactMatch) {
      setField(activeAllocation.id, "operationTypeId", exactMatch.id, activeAllocation);
      setField(activeAllocation.id, "operationType", exactMatch.name, activeAllocation);
      setOperationTypeSearch(exactMatch.name);
      setSimpleCategoryPopoverSearch(exactMatch.name);
      setSimpleCategoryPopoverOpen(false);
      return exactMatch;
    }
    const toastId = toast.loading("Création de la catégorie...");
    try {
      const movementId = await ensureBankMovement();
      if (!movementId) {
        toast.error("Impossible de créer le mouvement bancaire associé", { id: toastId });
        return null;
      }
      const created = await opTypes.createOperationTypeMutation.mutateAsync({
        name: query, budgetType, nature: nature ?? undefined, bankMovementId: movementId,
      });
      if (created?.id) {
        setField(activeAllocation.id, "operationTypeId", created.id, activeAllocation);
        setField(activeAllocation.id, "operationType", created.name, activeAllocation);
        setOperationTypeSearch(created.name);
        setSimpleCategoryPopoverSearch(created.name);
        setSimpleCategoryPopoverOpen(false);
        toast.success(`Catégorie "${created.name}" créée`, { id: toastId });
      }
      return created;
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error), { id: toastId });
      return null;
    }
  };

  const handleSaveEditorAllocation = async (allocation: LedgerAllocation, onComplete: () => void) => {
    const resolvedOperationType = await handleFindOrCreateOperationType();
    const state = getState(allocation);
    try {
      const isStock = (state.budgetType ?? "").toUpperCase() === "STOCK";
      await updateMutation.mutateAsync({
        id: allocation.id,
        payload: {
          bankMovementId: allocation.bankMovementId ?? bankMovementId,
          companyAccountId: settlementDraft?.bankAccountId || settlement?.bankAccount?.id || null,
          date: state.date || null,
          budgetType: state.budgetType || null,
          operationType: state.operationType || null,
          operationTypeId: (resolvedOperationType?.id ?? state.operationTypeId) || null,
          description: state.description || null,
          debit: parseNullableNumber(state.debit),
          credit: parseNullableNumber(state.credit),
          supplierId: simpleEditorSelectedSupplierId || null,
          ...(isStock
            ? {
              stockReferenceId: allocation.stockReferenceId || null,
              stockDebitQuantity: parseNullableNumber(state.stockDebitQuantity),
              stockCreditQuantity: parseNullableNumber(state.stockCreditQuantity),
            }
            : {}),
        },
      });
      onComplete();
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
      if (isServerRejection(error)) {
        revertAllocationDraft(allocation.id);
        if (getActiveEditorAllocation()?.id === allocation.id) {
          setSimpleEditorSelectedSupplierId(allocation.supplierId ?? "");
          setSimpleEditorSupplierSearch(allocation.supplierName ?? "");
        }
      }
    }
  };

  const handleSaveSimpleEditor = async () => {
    if (!selectedSimpleAllocation) return;
    await handleSaveEditorAllocation(selectedSimpleAllocation, closeSimpleEditor);
  };
  const handleSaveReimbursementEditor = async () => {
    if (!selectedReimbursementAllocation) return;
    await handleSaveEditorAllocation(selectedReimbursementAllocation, closeReimbursementEditor);
  };
  const handleSaveEcommerceEditor = async () => {
    if (!selectedEcommerceAllocation) return;
    await handleSaveEditorAllocation(selectedEcommerceAllocation, closeEcommerceEditor);
  };
  const handleSaveStockEditor = async (payload: StockEditorSavePayload): Promise<StockEditorSaveOutcome> => {
    if (!selectedStockAllocation) return "failed";
    try {
      await updateMutation.mutateAsync({
        id: selectedStockAllocation.id,
        payload: {
          bankMovementId: selectedStockAllocation.bankMovementId ?? bankMovementId,
          date: payload.date, budgetType: "STOCK",
          stockReferenceId: payload.stockReferenceId, supplierId: payload.supplierId,
          stockDebitQuantity: payload.stockDebitQuantity, stockCreditQuantity: payload.stockCreditQuantity,
          stockExpiresOn: payload.stockExpiresOn,
        },
      });
      closeStockEditor();
      return "saved";
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
      return isServerRejection(error) ? "rejected" : "failed";
    }
  };

  const openRulesManager = () => {
    const activeAllocation = getActiveEditorAllocation();
    const initialBudgetType = activeAllocation
      ? resolveBudgetType(getState(activeAllocation).budgetType || activeAllocation.budgetType)
      : selectedSimpleBudgetType;
    const currentCategoryId = activeAllocation ? (getState(activeAllocation).operationTypeId || activeAllocation.operationTypeId || "") : "";
    const currentCategoryName = activeAllocation ? (getState(activeAllocation).operationType || activeAllocation.operationTypeName || "") : "";
    rules.openRulesManager({
      initialBudgetType, currentCategoryId, currentCategoryName, defaultKeyword: "",
    });
  };

  const handleDrawerOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen && isSettlementBusy) return;
    onOpenChange(newOpen);
  }, [isSettlementBusy, onOpenChange]);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    settlementId,
    bankMovementId,
    onOperationTypeRemoved,
    settlementDraft,
    setSettlementField,
    isSettlementBusy,
    canSaveSettlement,
    handleSaveSettlement,
    handleDeleteSettlement,
    handleDrawerOpenChange,
    // Settlement accounts
    accountPopoverOpen, setAccountPopoverOpen, accountSearch, setAccountSearch,
    bankAccounts: filteredBankAccounts, isBankAccountsLoading, selectedAccount,
    // Settlement payment methods
    paymentMethodPopoverOpen, setPaymentMethodPopoverOpen, paymentMethodSearch, setPaymentMethodSearch,
    filteredPaymentMethods, isPaymentMethodsLoading,
    // Delete dialog
    isDeleteDialogOpen, setIsDeleteDialogOpen, isDeleteBusy,
    // Linked movement
    linkedMovement, isLoadingLinkedMovement,
    movementSearchQuery, handleMovementSearchChange,
    movementSearchResults, isSearchingMovement,
    attachingMovementId, isDetachingMovement,
    handleAttachMovement, handleDetachMovement,
    // Allocations list
    allocations, isLoading: isLoadingAllocations,
    getState, setField, isAllocationDirty,
    handleSave: (allocation: LedgerAllocation) => void handleSave(allocation),
    deleteAllocation: (id: string) => void (async () => {
      setDeletingAllocationId(id);
      try {
        await deleteMutation.mutateAsync(id);
      }
      catch (error) {
        toast.error(extractApiErrorMessage(error));
        setDeletingAllocationId(null);
      }
    })(),
    isBusy,
    isCreatePending: isAddingAllocation,
    deletingAllocationId,
    operationPopoverAllocationId, setOperationPopoverAllocationId,
    operationPopoverSearchByAllocationId, setOperationPopoverSearchByAllocationId,
    operationSearchBusyByAllocationId, markOperationSearchBusy,
    operationTypesByBudget: opTypes.operationTypesByBudget,
    handleFindOrCreateOperationTypeForAllocation: (allocation: LedgerAllocation, forcedQuery?: string, forcedNature?: OperationTypeNature) =>
      void handleFindOrCreateOperationTypeForAllocation(allocation, forcedQuery, forcedNature),
    isCreateOperationTypePending: opTypes.isCreateOperationTypePending,
    totalDebit, totalCredit, totalToAllocate, remainingToAllocate,
    // Add allocation
    handleAdd: (type: "simple" | "activity" | "stock") => void handleAdd(type),
    refundSearchOpen, setRefundSearchOpen, handleAddRefund,
    ecommerceSearchOpen, setEcommerceSearchOpen, handleAddEcommerceOperation,
    // Simple editor
    simpleEditorAllocationId, setSimpleEditorAllocationId,
    selectedSimpleAllocation, simpleEditorRow, closeSimpleEditor,
    isSimpleEditorBusy: opTypes.isSimpleEditorBusy || updateMutation.isPending,
    isLoadingOperationTypes: opTypes.isLoadingOperationTypes,
    isSimpleCategorySearching,
    operationTypeSearch, setOperationTypeSearch,
    simpleCategoryPopoverOpen, setSimpleCategoryPopoverOpen,
    simpleCategoryPopoverSearch, setSimpleCategoryPopoverSearch,
    markSimpleCategorySearchBusy, simpleCategoryContainerRef,
    operationTypes: opTypes.operationTypes,
    handleSaveSimpleEditor: () => void handleSaveSimpleEditor(),
    handleFindOrCreateOperationType: () => void handleFindOrCreateOperationType(),
    // Supplier
    simpleEditorSuppliers, simpleEditorSupplierSearch,
    setSimpleEditorSupplierSearch: handleSimpleEditorSupplierSearchChange,
    isLoadingSimpleEditorSuppliers, simpleEditorSelectedSupplierId,
    setSimpleEditorSelectedSupplierId,
    isSimpleEditorSupplierPopoverOpen, setIsSimpleEditorSupplierPopoverOpen,
    simpleEditorSupplierContainerRef,
    // Reimbursement editor
    reimbursementEditorAllocationId, setReimbursementEditorAllocationId,
    selectedReimbursementAllocation, reimbursementEditorRow, closeReimbursementEditor,
    handleSaveReimbursementEditor: () => void handleSaveReimbursementEditor(),
    // Ecommerce editor
    ecommerceEditorAllocationId, setEcommerceEditorAllocationId,
    selectedEcommerceAllocation, ecommerceEditorRow, closeEcommerceEditor,
    handleSaveEcommerceEditor: () => void handleSaveEcommerceEditor(),
    // Stock editor
    stockEditorAllocationId, setStockEditorAllocationId,
    selectedStockAllocation, closeStockEditor, handleSaveStockEditor,
    // Category manager
    isCategoryManagerOpen: opTypes.isCategoryManagerOpen, setIsCategoryManagerOpen: opTypes.setIsCategoryManagerOpen,
    selectedSimpleBudgetType,
    categoryManagerFilter: opTypes.categoryManagerFilter, setCategoryManagerFilter: opTypes.setCategoryManagerFilter,
    inactiveOperationTypes: opTypes.inactiveOperationTypes, isLoadingInactiveOperationTypes: opTypes.isLoadingInactiveOperationTypes,
    handleDisableOperationType: opTypes.handleDisableOperationType,
    handleEnableOperationType: opTypes.handleEnableOperationType,
    handleRenameOperationType: opTypes.handleRenameOperationType,
    handleUpdateAccountNumber: opTypes.handleUpdateAccountNumber,
    handleMergeOperationType: opTypes.handleMergeOperationType,
    handleDeleteOperationType: opTypes.handleDeleteOperationType,
    // Rules manager
    isRulesManagerOpen: rules.isRulesManagerOpen, setIsRulesManagerOpen: rules.setIsRulesManagerOpen,
    openRulesManager,
    ruleName: rules.ruleName, setRuleName: rules.setRuleName,
    ruleConditions: rules.ruleConditions, updateRuleCondition: rules.updateRuleCondition,
    addRuleCondition: rules.addRuleCondition, removeRuleCondition: rules.removeRuleCondition,
    ruleBudgetType: rules.ruleBudgetType, setRuleBudgetType: rules.setRuleBudgetType,
    ruleOperationTypeId: rules.ruleOperationTypeId, setRuleOperationTypeId: rules.setRuleOperationTypeId,
    ascOperationTypes: opTypes.ascOperationTypes, aepOperationTypes: opTypes.aepOperationTypes,
    ruleActionInProgress: rules.ruleActionInProgress,
    handleCreateLocalRule: rules.handleCreateLocalRule,
    handleToggleLocalRuleActive: rules.handleToggleLocalRuleActive,
    handleDeleteLocalRule: rules.handleDeleteLocalRule,
    sortedLocalRules: rules.sortedLocalRules,
    isCategorizationRulesLoading: rules.isCategorizationRulesLoading,
  };
};
